'use client';

import * as React from 'react';
import { MessageSquare, Archive } from 'lucide-react';
import { cn } from '../utils/cn';
import { Tooltip } from './tooltip';

export type ChatSwitcherProps = {
  showArchived: boolean;
  onToggle: (showArchived: boolean) => void;
  className?: string;
};

/**
 * ChatSwitcher component
 *
 * Pill-shaped toggle to switch between active chats and archived chats.
 * Apple-inspired design with smooth animations.
 */
export const ChatSwitcher = React.forwardRef<HTMLDivElement, ChatSwitcherProps>(
  ({ showArchived, onToggle, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex items-center gap-0 p-0.5',
          'bg-muted/60 rounded-lg',
          'transition-all duration-200',
          className
        )}
        data-testid="chat-switcher"
      >
        <button
          type="button"
          onClick={() => onToggle(false)}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 rounded-md',
            'transition-all duration-200',
            'text-xs font-medium whitespace-nowrap',
            !showArchived
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
          aria-label="Show active chats"
          aria-pressed={!showArchived}
          data-testid="chat-switcher-active"
        >
          <MessageSquare className="w-3 h-3" />
          <span>Chats</span>
        </button>

        <button
          type="button"
          onClick={() => onToggle(true)}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1 rounded-md',
            'transition-all duration-200',
            'text-xs font-medium whitespace-nowrap',
            showArchived
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          )}
          aria-label="Show archived chats"
          aria-pressed={showArchived}
          data-testid="chat-switcher-archived"
        >
          <Archive className="w-3 h-3" />
          <span>Archived</span>
        </button>
      </div>
    );
  }
);

ChatSwitcher.displayName = 'ChatSwitcher';
